<script lang="ts">
  import Form from "../components/Form.svelte";
  import Initial from "./RespondToFinanceCompanyTask/Initial.svelte";
  import UploadFinanceAgreement from "./RespondToFinanceCompanyTask/UploadFinanceAgreement.svelte";
  import ConsentCreditCheck from "./RespondToFinanceCompanyTask/ConsentCreditCheck.svelte";
  import ConfirmFinanceDate from "./RespondToFinanceCompanyTask/ConfirmFinanceDate.svelte";
  import ConfirmClientName from "./RespondToFinanceCompanyTask/ClientName/ConfirmClientName.svelte";
  import ChangeClientName from "./RespondToFinanceCompanyTask/ClientName/ChangeClientName.svelte";
  import ClientIsOtherPerson from "./RespondToFinanceCompanyTask/ClientName/ClientIsOtherPerson.svelte";
  import ConfirmDateOfBirth from "./RespondToFinanceCompanyTask/ConfirmDateOfBirth.svelte";
  import ConfirmClientAddress from "./RespondToFinanceCompanyTask/ClientAddress/ConfirmClientAddress.svelte";
  import ChangeClientAddress from "./RespondToFinanceCompanyTask/ClientAddress/ChangeClientAddress.svelte";
  import ConfirmFinanceCompany from "./RespondToFinanceCompanyTask/FinanceCompany/ConfirmFinanceCompany.svelte";
  import ChangeFinanceCompany from "./RespondToFinanceCompanyTask/FinanceCompany/ChangeFinanceCompany.svelte";
  import VehicleNotFinanced from "./RespondToFinanceCompanyTask/FinanceCompany/VehicleNotFinanced.svelte";
  import CloseCaseConfirmation from "./RespondToFinanceCompanyTask/CloseCaseConfirmation.svelte";
  import ConsentConfirmation from "./RespondToFinanceCompanyTask/ConsentConfirmation.svelte";
  import TaskConfirmation from "./RespondToFinanceCompanyTask/TaskConfirmation.svelte";
  import { localizeDate } from "../lib/date";
  import type { Component } from "svelte";

  let {
    action,
    createUploadSessionUrl,
    financeAgreementUploadAllowedExtensions,
    financeCompany,
    financeCompanyOptions,
    financeAgreementStartDate,
    clientName,
    clientNameUploadAllowedExtensions,
    dateOfBirth,
    clientAddress,
    clientAddressIncomplete,
    creditCheckable,
    displayConfirmation,
    errors,
    elementsToCorrect,
    kountSessionId
  }: {
    action: string;
    createUploadSessionUrl: string;
    financeAgreementUploadAllowedExtensions: string[];
    financeCompany: string;
    financeCompanyOptions: string[];
    financeAgreementStartDate: string | null;
    clientName: { [key: string]: string };
    clientNameUploadAllowedExtensions: string[];
    dateOfBirth: string;
    clientAddress: { [key: string]: string };
    clientAddressIncomplete: boolean;
    creditCheckable: boolean;
    displayConfirmation?: "close_case" | "credit_check" | null;
    errors: { [key: string]: string } | null;
    elementsToCorrect: string | null;
    kountSessionId: string;
  } = $props();

  const clientAddressStep : Component = clientAddressIncomplete ? ChangeClientAddress : ConfirmClientAddress;

  // For now, if multiple elements are passed (e.g. "Date of Birth;Address Verification"),
  // correctOneElement won't match, so we will show all steps.
  const correctOneElement : Component | undefined = ({
    "Full Name / Maiden Name": ConfirmClientName,
    "Date of Birth": ConfirmDateOfBirth,
    "Address Verification": clientAddressStep,
  }[elementsToCorrect]);
  let currentStep = $state<Component>(correctOneElement || Initial);

  $effect(() => {
    if (displayConfirmation === "close_case") {
      currentStep = CloseCaseConfirmation;
    } else if (displayConfirmation === "credit_check") {
      currentStep = ConsentConfirmation;
    }
  })

  let answers = $state([]);
  let allInformationIsUnchanged = $derived(answers.every((answer) => answer.status !== "changed"));
  let financeAgreementStartDateFromClient = $state(financeAgreementStartDate);
  let form = $state<HTMLFormElement>();
  let modalBox = $derived(form?.closest(".modal-box"));
  let dialog = $derived(form?.closest("dialog"));
  let currentAnswer = null;

  function answer({ nextStep, status }) {
    currentAnswer = { nextStep, status };
  }

  function back() {
    currentStep = answers.pop().step;
    modalBox.scrollTop = 0;
  }

  function updateFinanceAgreementStartDate(newDate) {
    financeAgreementStartDateFromClient = newDate;
  }

  // Prevent the form from being submitted (by pressing Enter key for example) when not on the final step
  function handleSubmit(event: Event) {
    const finalSteps : Component[] = [
        UploadFinanceAgreement,
        ConsentCreditCheck,
        ClientIsOtherPerson,
        VehicleNotFinanced,
        TaskConfirmation,
      ]
    if (!finalSteps.includes(currentStep)) {
      event.preventDefault();
    }
    if (currentAnswer) {
      const { nextStep, status } = currentAnswer;
      currentAnswer = null;
      answers = [...answers, { step: currentStep, status: status }];
      currentStep = nextStep;
      modalBox.scrollTop = 0;
    }
  }

  function makeStep(StepComponent, propsFn = null) {
    return [
      StepComponent,
      ()=>{
        if(!propsFn) return {};
        let nextStepProp;
        const [props, nextSteps] = propsFn();
        // A single component
        if(typeof nextSteps === "function") nextStepProp = {nextStep: nextSteps};
        // A hash of two or more components
        else if(nextSteps) nextStepProp = {nextSteps};
        // No next step(s)
        else nextStepProp = {};
        return Object.assign(props, nextStepProp);
      }
    ];
  }
  const steps = [
    makeStep(Initial, ()=>[
      {financeCompany, creditCheckable},
      {
        next: UploadFinanceAgreement,
        credit: ConsentCreditCheck,
        questions: ConfirmFinanceDate,
      }
    ]),
    makeStep(ConsentCreditCheck, ()=>[
      {kountSessionId}
    ]),
    makeStep(UploadFinanceAgreement, ()=>[
      {createUploadSessionUrl, financeAgreementUploadAllowedExtensions, required: currentStep === UploadFinanceAgreement, errors}
    ]),
    makeStep(ConfirmFinanceDate, ()=>[
      {updateFinanceAgreementStartDate, financeAgreementStartDate},
      ConfirmClientName
    ]),
    makeStep(ConfirmClientName, ()=>[
      {clientName},
      {
        next: correctOneElement ? TaskConfirmation : ConfirmDateOfBirth,
        name: ChangeClientName,
        other: ClientIsOtherPerson
      }
    ]),
    makeStep(ChangeClientName, ()=>[
      {clientName, financeAgreementStartDate: localizeDate(financeAgreementStartDateFromClient), createUploadSessionUrl, clientNameUploadAllowedExtensions},
      correctOneElement ? TaskConfirmation : ConfirmDateOfBirth
    ]),
    makeStep(ClientIsOtherPerson),
    makeStep(ConfirmDateOfBirth, ()=>[
      {dateOfBirth, dateOfBirthLocalized: localizeDate(dateOfBirth)},
      correctOneElement ? TaskConfirmation : clientAddressStep
    ]),
    makeStep(ConfirmClientAddress, ()=>[
      {clientAddress, financeAgreementStartDateLocalized: localizeDate(financeAgreementStartDateFromClient)},
      {
        next: correctOneElement ? TaskConfirmation : ConfirmFinanceCompany,
        change: ChangeClientAddress
      }
    ]),
    makeStep(ChangeClientAddress, ()=>[
      {clientAddress, clientAddressIncomplete, financeAgreementStartDateLocalized: localizeDate(financeAgreementStartDateFromClient)},
      correctOneElement ? TaskConfirmation : ConfirmFinanceCompany
    ]),
    makeStep(ConfirmFinanceCompany, ()=>[
      {financeCompany},
      {
        next: TaskConfirmation,
        change: ChangeFinanceCompany,
        close: VehicleNotFinanced
      }
    ]),
    makeStep(ChangeFinanceCompany, ()=>[
      {financeCompany, financeCompanyOptions},
      TaskConfirmation
    ]),
    makeStep(VehicleNotFinanced),
    makeStep(TaskConfirmation, ()=>[
      {allInformationIsUnchanged}
    ]),
    makeStep(CloseCaseConfirmation),
    makeStep(ConsentConfirmation),
  ]
</script>

<Form
  method="patch"
  {action}
  class="ui-content-options text-base"
  onsubmit={handleSubmit}
  bind:form={form}
>
  {#each steps as [StepComponent, props] (StepComponent)}
    {@const inactive = currentStep !== StepComponent}
    <div
      class:invisible={inactive}
      class="min-w-0 ui-rhythm"
    >
      <StepComponent {answer} {inactive} back={answers.length > 0 ? back : null} {...props()} />
    </div>
  {/each}
  {#if correctOneElement}
    <input type="hidden" name="finance_agreement[option]" value="questions">
  {/if}
</Form>
